package diana.soleil.design.iraniancanadienbazzar.core.designsystem.theme

import androidx.compose.runtime.Immutable
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color

/**
 * Rich brand-adjacent gradient tokens for full-bleed "story" surfaces (the Hidden Gems swipe
 * readers). Access via `AppTheme.gradients`. These are the ONE place raw gradient colors are
 * allowed to live (theme layer) — screens/components must reference [AppGradients.storyCards]
 * via [AppGradients.storyCard], never build a `Brush` from literals inline.
 *
 * Every gradient is dark enough at the text band that white ([AppColors.onPrimary]) body text
 * stays legible. Diagonal (top-start → bottom-end) for a modern, dynamic feel; mirrors correctly
 * in RTL because the direction is expressed in the layout, not baked here.
 */
@Immutable
data class AppGradients(
    /** A tasteful, varied palette; callers pick by index (page) or by category via [storyCard]. */
    val storyCards: List<Brush>,
) {
    /** Stable pick: same key always maps to the same gradient (wraps the palette). */
    fun storyCard(key: Int): Brush = storyCards[((key % storyCards.size) + storyCards.size) % storyCards.size]
}

private fun diagonal(top: Color, bottom: Color) = Brush.linearGradient(listOf(top, bottom))

val appGradients: AppGradients = AppGradients(
    storyCards = listOf(
        diagonal(Color(0xFF0A7C86), Color(0xFF063A40)), // teal (brand)
        diagonal(Color(0xFF1E7A5F), Color(0xFF0C3B2E)), // forest
        diagonal(Color(0xFFB86A1E), Color(0xFF7A3A12)), // amber / terracotta
        diagonal(Color(0xFF6A2C74), Color(0xFF3B1147)), // plum
        diagonal(Color(0xFF35508F), Color(0xFF1A2649)), // indigo
        diagonal(Color(0xFFA63A55), Color(0xFF5E1E30)), // rose
    ),
)
